import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ErrorPage } from '@/components/common/ErrorPage';
import { Spinner } from '@/components/generic';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { VideoThemeProvider } from '@/contexts/VideoThemeProvider';
import { PRIVATE_PAGE_ROUTES } from '@/enums/RouterEnums';
import { TABS } from '@/enums/ThemeTabsEnum';
import { showToast, ToastType } from '@/helpers/toast';
import { VideoThemeClips } from '@/pages/VideoTheme/Clips';
import { Header } from '@/pages/VideoTheme/Header';
import { TextOverlay } from '@/pages/VideoTheme/TextOverlay';
import { VideoThemeDetail } from '@/pages/VideoTheme/VideoThemeDetail';
import {
  useDeleteVideoThemeMutation,
  useGetVideoThemeById,
  useUpdatePublish
} from '@/services/videoTheme.service';
import { useGetClipsByVideoThemeIdQuery } from '@/services/videoThemeClips.service';
import { VideoThemeClip } from '@/types';

const CLIP_TYPES = {
  Intro: 'Intro',
  Background: 'Background',
  Middle: 'Middle',
  Outro: 'Outro'
};

export const VideoThemeView = () => {
  const navigate = useNavigate();
  const { themeId } = useParams();
  const [error, setError] = useState<number>();
  const {
    data: themeData,
    isLoading,
    refetch,
    isError
  } = useGetVideoThemeById(Number(themeId), {
    onSuccess: () => undefined,
    onError: (error) => setError(error.status)
  });
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<string>(TABS.THEME_DETAILS);
  const [t] = useTranslation('common');
  const { data: videoThemeClips } = useGetClipsByVideoThemeIdQuery(
    Number(themeId),
    undefined,
    !!themeData
  );

  const containsRequiredClips = () => {
    if (!videoThemeClips?.data) {
      return false;
    }

    const hasIntro = videoThemeClips.data.some(
      (clip: VideoThemeClip) => clip.type === CLIP_TYPES.Intro
    );
    const hasOutro = videoThemeClips.data.some(
      (clip: VideoThemeClip) => clip.type === CLIP_TYPES.Outro
    );
    const hasBackground = videoThemeClips.data.some(
      (clip: VideoThemeClip) => clip.type === CLIP_TYPES.Background
    );

    return hasIntro && hasOutro && hasBackground;
  };

  const { mutate: updatePublish } = useUpdatePublish(queryClient, {
    onSuccess: () => {
      navigate(`${PRIVATE_PAGE_ROUTES.THEMES}/${themeId}`);
      showToast(ToastType.SUCCESS, t('themePublish'));
      refetch();
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages?.[0].DisplayMessage);
    }
  });

  const handlePublish = () => {
    const payload = {
      id: Number(themeId ?? 0),
      isPublish: true
    };

    containsRequiredClips()
      ? updatePublish(payload)
      : showToast(ToastType.ERROR, t('unableToPublish'));
  };
  const { mutate: deleteTheme } = useDeleteVideoThemeMutation(queryClient, {
    onSuccess: () => {
      setIsOpenDeleteModal(false);
      handleCloseDeleteModal();
      navigate(PRIVATE_PAGE_ROUTES.THEMES);
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const handleCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
  };

  const content = () => {
    switch (currentTab) {
      case TABS.THEME_DETAILS:
        return <VideoThemeDetail />;
      case TABS.CLIPS:
        return <VideoThemeClips id={themeId ?? '0'} />;
      case TABS.TEXT_OVERLAYS:
        return <TextOverlay id={themeId ?? '0'} />;
      default:
        return '';
    }
  };

  useEffect(() => {
    if (
      localStorage.getItem('currentTabTheme') === null ||
      localStorage.getItem('currentTabTheme') === '' ||
      localStorage.getItem('currentTabTheme') !== TABS.THEME_DETAILS
    ) {
      localStorage.setItem('currentTabTheme', TABS.THEME_DETAILS);
    }
  }, []);

  return (
    <div className='flex size-full flex-col items-center px-3'>
      {isLoading ? (
        <Spinner />
      ) : error ? (
        <ErrorPage code={error} />
      ) : (
        !isError && (
          <VideoThemeProvider themeId={Number(themeId)}>
            <Header
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              setIsDeleteModalOpen={setIsOpenDeleteModal}
              handlePublish={handlePublish}
            />
            {content()}
            {themeId && (
              <ConfirmationModal
                isOpen={isOpenDeleteModal}
                message={t('deleteMessage', { item: themeData?.data.name })}
                onClose={handleCloseDeleteModal}
                onConfirm={() => deleteTheme(themeData?.data.id)}
              />
            )}
          </VideoThemeProvider>
        )
      )}
    </div>
  );
};
